import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {useTranslation} from 'react-i18next';

import CanvasStrip from '../components/CanvasStrip';

interface PreviewBannerProps {
    /** The first answer, or a refresh slow enough to notice. */
    calculating: boolean;
    failed: boolean;
    /** The audience the flow reads first. */
    readingName?: string;
    onRetry: () => void;
    onExit: () => void;
}

/**
 * Preview's blue strip: the mode's one explanation and one progress indicator. It cannot be dismissed, only left
 * (Exit, the header button, Esc — unadvertised on purpose): a mode that always says what it is reassures staff.
 */
const PreviewBanner = ({calculating, failed, readingName, onRetry, onExit}: PreviewBannerProps) => {
    const {t} = useTranslation();
    const showFailure = failed && !calculating;

    const message = calculating ? (
        readingName
            ? t('Reading the audience “{{name}}”…', {name: readingName})
            : t('Working out who it would reach…')
    ) : showFailure ? (
        t('The preview could not be calculated.')
    ) : (
        <>
            <Box component={'span'} sx={{fontWeight: 600}}>{t('Preview')}</Box>
            {' · '}
            {t('Preview banner')}
        </>
    );

    return (
        <CanvasStrip
            tone={showFailure ? 'error' : 'info'}
            icon={calculating
                ? <CircularProgress size={16} thickness={5} color={'inherit'}/>
                : showFailure ? <ErrorOutlineRoundedIcon/> : <VisibilityOutlinedIcon/>}
            message={message}
            busy={calculating}
            actions={(
                <>
                    {showFailure && (
                        <Button size={'small'} color={'error'} onClick={onRetry} sx={{flexShrink: 0}}>
                            {t('Retry')}
                        </Button>
                    )}
                    <Button
                        size={'small'}
                        color={showFailure ? 'error' : 'info'}
                        onClick={onExit}
                        sx={{flexShrink: 0, whiteSpace: 'nowrap'}}
                    >
                        {t('Exit preview')}
                    </Button>
                </>
            )}
        />
    );
};

export default PreviewBanner;
